// Utility function to check if gtag is loaded
function isGtagLoaded() {
  return typeof gtag === "function";
}

// Utility function to track events
function trackEvent(eventName, eventParams) {
  if (!isGtagLoaded()) {
    console.warn(`gtag is not loaded. Event not tracked: ${eventName}`);
    return;
  }
  // if in dev mode, log the event
  if (process.env.NODE_ENV === 'development') {
    console.log('tracking event', eventName, eventParams);
  }
  gtag("event", eventName, eventParams);
}

// Create an analytics session to track related events
const analyticsSession = {
  currentScene: null,
  
  /**
   * Call this when a scene starts.
   *
   * @param {string} scene_id - The unique identifier for the scene.
   * @param {string} scene_title - The title of the scene.
   * @param {string} scene_source - The source of the scene.
   */
  startScene({ scene_id, scene_title, scene_source }) {
    // End previous scene if exists
    if (this.currentScene) {
      this.endScene(this.currentScene);
    }

    this.currentScene = {
      scene_id,
      scene_title,
      scene_source,
      start_time: new Date().toISOString()
    };
  },

  /**
   * Track when a scene is exited.
   *
   * @param {string} scene_id - The unique identifier for the scene.
   * @param {string} scene_title - The title of the scene.
   */
  endScene({ scene_id, scene_title }) {
    if (!this.currentScene) return;

    const { start_time, scene_source } = this.currentScene;
    const end_time = new Date().toISOString();
    const duration = Math.round((new Date(end_time) - new Date(start_time)) / 1000);

    // Send to Google Analytics
    trackEvent('tour_scene_view', {
      scene_id,
      scene_title,
      start_time,
      end_time,
      duration,
      scene_source
    });

    this.currentScene = null;
  },

  /**
   * Set the source of the current scene.
   *
   * @param {string} source - The source of the scene (e.g., 'grid', 'next', 'previous', 'hotspot').
   */
  setSource(scene_source) {
    if (this.currentScene) {
      this.currentScene.scene_source = scene_source;
    }
  }
};

/**
 * Call this when a scene starts.
 *
 * @param {string} scene_id - The unique identifier for the scene.
 * @param {string} scene_title - The title of the scene.
 * @param {string} scene_source - The source of the scene.
 */
export const trackSceneViewStart = (data) => analyticsSession.startScene(data);

/**
 * Track when a scene is exited.
 *
 * @param {string} scene_id - The unique identifier for the scene.
 * @param {string} scene_title - The title of the scene.
 */
export const trackSceneViewEnd = (data) => analyticsSession.endScene(data);

/**
 * Set the source of the current scene.
 *
 * @param {string} source - The source of the scene (e.g., 'grid', 'next', 'previous', 'hotspot').
 */
export const setTrackSceneSource = (source) => analyticsSession.setSource(source);

/**
 * Track the final scene view. This is useful when the user exits the tour.
 *
 * Uses the last scene that was tracked with trackSceneViewStart.
 */
export function trackSceneViewFinal() {
  if (analyticsSession.currentScene) {
    analyticsSession.endScene(analyticsSession.currentScene);
  }
}

/**
 * Track when a hotspot is clicked.
 *
 * @param {string} hotspotId - The unique identifier for the hotspot.
 * @param {string} hotspotTitle - The title of the hotspot.
 * @param {string} hotspotType - The type of hotspot (e.g., 'link', 'popup').
 */
export function trackHotspotClick({ hotspot_id, hotspot_type, hotspot_title }) {
  trackEvent("tour_hotspot_click", {
    hotspot_id,
    hotspot_title,
    hotspot_type,
  });
}

/**
 * Track when a Link Button (Request Information or Apply button) is clicked.
 *
 * @param {string} link_text - The text displayed on the button.
 * @param {string} link_url - The URL the button redirects to.
 * @param {string} scene_id - The unique identifier for the scene.
 * @param {string} scene_title - The title of the scene.
 */
export function trackCallToActionClick({ link_text, link_url, scene_id, scene_title }) {
  trackEvent("tour_call_to_action_click", {
    link_text,
    link_url,
    scene_id,
    scene_title,
  });
}

/**
 * Track when a hamburger menu link is clicked.
 *
 * @param {string} link_text - The text of the menu link.
 * @param {string} link_url - The URL the link redirects to, if any.
 */
export function trackHamburgerMenuClick({ link_text, link_url = null }) {
  trackEvent("tour_hamburger_menu_click", {
    link_text,
    link_url,
  });
}

/**
 * Track when the logo is clicked.
 *
 * @param {string} link_url - The URL the logo redirects to.
 */
export function trackLogoClick({ link_url }) {
  trackEvent("tour_logo_click", {
    link_url,
  });
}
